import React, { useState } from "react";
import { useGetFamilyTreeQuery } from "../../../../store/slices/api";
import useSurnameBranches from "./useSurnameBranches";
import BranchCard from "./BranchCard";
import { SharedFeatureError } from "../../../shared";

const INITIAL_BRANCHES = 25;

function SurnameBranches() {
  const { data: familyTree, isLoading, isFetching, isError, refetch } = useGetFamilyTreeQuery();
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("count");
  const [visibleCount, setVisibleCount] = useState(INITIAL_BRANCHES);

  const { originalBranches, extendedBranches, totalPeople, totalSurnames, noSurnameCount } =
    useSurnameBranches(familyTree, search, sortOrder);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setVisibleCount(INITIAL_BRANCHES);
  };

  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    setVisibleCount(INITIAL_BRANCHES);
  };

  // Also while "Try again" runs: RTK Query then reports neither loading nor
  // error and no data, which would read as "No family tree data available."
  if (isLoading || (!familyTree && isFetching)) {
    return (
      <div className="flex items-center justify-center py-[40px]">
        <span className="text-dupontGray dark:text-darkTextSecondary text-lg">Loading family data...</span>
      </div>
    );
  }

  // A failed load is not an empty tree: say so, and offer to try again.
  if (isError && !familyTree) {
    return <SharedFeatureError onRetry={refetch} />;
  }

  if (!familyTree) {
    return (
      <div className="flex items-center justify-center py-[40px]">
        <span className="text-dupontGray dark:text-darkTextSecondary text-lg">No family tree data available.</span>
      </div>
    );
  }

  const visibleExtended = extendedBranches.slice(0, visibleCount);
  const hasMore = visibleCount < extendedBranches.length;

  return (
    <div className="flex flex-col gap-y-[20px]">
      {/* Summary */}
      <div className="text-center text-dupontGray dark:text-darkTextSecondary text-sm">
        {totalSurnames} surnames across {totalPeople.toLocaleString()} individuals
        {noSurnameCount > 0 && (
          <span> ({noSurnameCount} with no surname recorded)</span>
        )}
      </div>

      {/* ===== ORIGINAL BRANCHES SECTION ===== */}
      {originalBranches.length > 0 && !search && (
        <div className="flex flex-col gap-y-[12px]">
          <div className="flex items-center gap-x-[12px]">
            <div className="h-[1px] flex-1 bg-dupontGold/40" />
            <h2 className="text-lg font-display font-semibold text-dupontBlue dark:text-darkText whitespace-nowrap">
              The Founding Line
            </h2>
            <div className="h-[1px] flex-1 bg-dupontGold/40" />
          </div>
          <p className="text-center text-[13px] text-dupontGray dark:text-darkTextSecondary">
            The original family — generations 1 through 3 of the tree
          </p>
          {originalBranches.map((branch) => (
            <BranchCard key={branch.key} branch={branch} variant="original" />
          ))}
        </div>
      )}

      {/* ===== EXTENDED FAMILIES SECTION ===== */}
      <div className="flex flex-col gap-y-[12px]">
        {!search && (
          <>
            <div className="flex items-center gap-x-[12px] mt-[8px]">
              <div className="h-[1px] flex-1 bg-dupontGold/40" />
              <h2 className="text-lg font-display font-semibold text-dupontBlue dark:text-darkText whitespace-nowrap">
                Extended Family
              </h2>
              <div className="h-[1px] flex-1 bg-dupontGold/40" />
            </div>
            <p className="text-center text-[13px] text-dupontGray dark:text-darkTextSecondary">
              {extendedBranches.length} surnames that joined the family through marriage
            </p>
          </>
        )}

        {/* Search + sort controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-[10px]">
          <input
            type="text"
            placeholder="Search surnames..."
            value={search}
            onChange={handleSearchChange}
            className="flex-1 px-[12px] py-[8px] border border-dupontGold/50 rounded-lg text-sm
                       focus:outline-none focus:border-dupontGold focus:ring-1 focus:ring-dupontGold
                       bg-white dark:bg-darkCard text-dupontBlue dark:text-darkText placeholder-dupontGray dark:placeholder-darkTextSecondary"
          />
          <div className="flex gap-x-[6px]">
            <button
              type="button"
              onClick={() => handleSortChange("count")}
              className={`px-[12px] py-[8px] rounded-lg text-sm cursor-pointer transition-colors ${
                sortOrder === "count"
                  ? "bg-dupontGold text-white"
                  : "bg-white dark:bg-darkCard text-dupontGray dark:text-darkTextSecondary border border-dupontGold/30 hover:border-dupontGold"
              }`}
            >
              Most People
            </button>
            <button
              type="button"
              onClick={() => handleSortChange("alpha")}
              className={`px-[12px] py-[8px] rounded-lg text-sm cursor-pointer transition-colors ${
                sortOrder === "alpha"
                  ? "bg-dupontGold text-white"
                  : "bg-white dark:bg-darkCard text-dupontGray dark:text-darkTextSecondary border border-dupontGold/30 hover:border-dupontGold"
              }`}
            >
              A&ndash;Z
            </button>
          </div>
        </div>

        {/* Search result count */}
        {search && (
          <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
            Showing {originalBranches.length + extendedBranches.length}{" "}
            {originalBranches.length + extendedBranches.length === 1 ? "surname" : "surnames"}{" "}
            matching &ldquo;{search}&rdquo;
          </div>
        )}

        {/* When searching, show original matches inline too */}
        {search && originalBranches.length > 0 && (
          <>
            {originalBranches.map((branch) => (
              <BranchCard key={branch.key} branch={branch} variant="original" />
            ))}
          </>
        )}

        {/* Extended branch cards */}
        <div className="flex flex-col gap-y-[10px]">
          {visibleExtended.map((branch) => (
            <BranchCard key={branch.key} branch={branch} />
          ))}
        </div>

        {/* No results */}
        {originalBranches.length === 0 && extendedBranches.length === 0 && search && (
          <div className="text-center text-dupontGray dark:text-darkTextSecondary py-[20px]">
            No surnames matching &ldquo;{search}&rdquo;
          </div>
        )}

        {/* Show more button */}
        {hasMore && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setVisibleCount((prev) => prev + INITIAL_BRANCHES)}
              className="px-[20px] py-[10px] rounded-lg text-sm text-dupontGold border border-dupontGold
                         hover:bg-dupontGold hover:text-white transition-colors cursor-pointer"
            >
              Show more ({extendedBranches.length - visibleCount} remaining)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SurnameBranches;
